import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { BrandPanel } from "@/components/auth/BrandPanel";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { APP_VERSION_LABEL, PRODUCT_NAME, LOGO_SRC } from "@/lib/brand";
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("auth.pwMismatch", "Passwords do not match."));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, newPassword: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(apiErrorMessage(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-300 pl-11 pr-11 py-3 text-sm outline-none focus:ring-2 focus:ring-[#118D89]/30 focus:border-[#118D89]";

  return (
    <div className="min-h-screen flex bg-white">
      <BrandPanel />
      <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-8">
        <div className="w-full max-w-md" data-testid="reset-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="bg-white border border-slate-200 rounded-xl p-1.5 shadow-sm">
              <img src={LOGO_SRC} alt="REASON" className="h-9 w-9 object-contain" />
            </div>
            <span className="font-extrabold text-lg text-slate-900">{PRODUCT_NAME}</span>
          </div>

          {done ? (
            <div data-testid="reset-success">
              <div className="h-14 w-14 rounded-full bg-[#E3F3F2] flex items-center justify-center mb-5">
                <CheckCircle2 className="h-7 w-7 text-[#0E8C84]" />
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900">{t("auth.resetDone", "Password updated")}</h1>
              <p className="text-slate-500 text-sm mt-2">{t("auth.resetDoneSub", "Redirecting you to sign in…")}</p>
            </div>
          ) : !token ? (
            <div data-testid="reset-invalid">
              <h1 className="text-2xl font-extrabold text-slate-900">{t("auth.invalidLink", "Invalid reset link")}</h1>
              <p className="text-slate-500 text-sm mt-2">{t("auth.invalidLinkSub", "This link is missing or expired. Request a new one.")}</p>
              <Link to="/forgot-password" className="inline-block mt-5 text-sm font-semibold text-[#118D89] hover:underline">
                {t("auth.requestNew", "Request a new link")}
              </Link>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h1 className="text-3xl font-extrabold text-slate-900">{t("auth.resetTitle", "Reset Password")}</h1>
              <p className="text-slate-500 text-sm mt-2 mb-7">{t("auth.resetSub", "Choose a new password for your account.")}</p>
              {error && (
                <div className="mb-5 text-sm rounded-xl bg-red-50 text-red-700 px-4 py-3 border border-red-100" data-testid="reset-error">
                  {error}
                </div>
              )}
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t("auth.newPassword", "New Password")}</label>
              <div className="relative mb-4">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-slate-400" />
                <input
                  type={show ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)} className={inputCls}
                  placeholder={t("auth.newPasswordPlaceholder", "At least 12 characters")} data-testid="reset-password"
                />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Toggle">
                  {show ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t("auth.confirmPassword", "Confirm Password")}</label>
              <div className="relative mb-6">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-slate-400" />
                <input
                  type={show ? "text" : "password"} required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} className={inputCls}
                  placeholder={t("auth.confirmPlaceholder", "Re-enter your new password")} data-testid="reset-confirm"
                />
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-[#0E8C84] hover:bg-[#0c7c74] text-white rounded-xl py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                data-testid="reset-submit">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("auth.updatePassword", "Update password")}
              </button>
            </form>
          )}

          <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-[#118D89] hover:underline mt-7" data-testid="reset-back">
            <ArrowLeft className="h-4 w-4" /> {t("auth.backToLogin", "Back to sign in")}
          </Link>

          <div className="flex items-center justify-between mt-8">
            <LanguageSwitcher />
            <span className="text-xs font-semibold text-slate-400">{APP_VERSION_LABEL}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
